import { isAfter, startOfDay } from 'date-fns';
import { z } from 'zod';

import { OverheadRecurrenceFrequency, OverheadType } from '../types/overhead';

export const OVERHEAD_NAME_MAX_LENGTH = 200;
export const OVERHEAD_NOTES_MAX_LENGTH = 500;

const VALIDATION_KEY_PREFIX = 'authenticated.overheads:validation';

/**
 * Amount sign rules per overhead type:
 * - Income: any non-zero value (negative = discount line on the invoice)
 * - Cost: strictly positive
 */
const isAmountValidForType = (type: OverheadType, amount: number): boolean => {
  if (isNaN(amount) || amount === 0) return false;
  if (type === OverheadType.Cost) return amount > 0;
  return true;
};

export const overheadFormSchema = z
  .object({
    type: z.nativeEnum(OverheadType),
    projectId: z.string().min(1, `${VALIDATION_KEY_PREFIX}.projectRequired`),
    name: z
      .string()
      .trim()
      .min(1, `${VALIDATION_KEY_PREFIX}.nameRequired`)
      .max(OVERHEAD_NAME_MAX_LENGTH, `${VALIDATION_KEY_PREFIX}.nameTooLong`),
    date: z.date({ message: `${VALIDATION_KEY_PREFIX}.dateRequired` }),
    amount: z.string().min(1, `${VALIDATION_KEY_PREFIX}.amountRequired`),
    recurrenceFrequency: z.nativeEnum(OverheadRecurrenceFrequency),
    recurrenceEndDate: z.date().nullable(),
    notes: z.string().max(OVERHEAD_NOTES_MAX_LENGTH, `${VALIDATION_KEY_PREFIX}.notesTooLong`),
  })
  .superRefine((values, ctx) => {
    const amount = Number(values.amount);
    if (!isAmountValidForType(values.type, amount)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['amount'],
        message:
          values.type === OverheadType.Cost
            ? `${VALIDATION_KEY_PREFIX}.amountPositive`
            : `${VALIDATION_KEY_PREFIX}.amountNonZero`,
      });
    }

    if (values.recurrenceFrequency !== OverheadRecurrenceFrequency.OneTime) {
      if (!values.recurrenceEndDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['recurrenceEndDate'],
          message: `${VALIDATION_KEY_PREFIX}.recurrenceEndDateRequired`,
        });
      } else if (!isAfter(startOfDay(values.recurrenceEndDate), startOfDay(values.date))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['recurrenceEndDate'],
          message: `${VALIDATION_KEY_PREFIX}.recurrenceEndDateAfterStart`,
        });
      }
    }
  });

export type OverheadFormValues = z.infer<typeof overheadFormSchema>;
